/**
 * Calculates the Levenshtein distance between two strings
 */
export const getLevenshteinDistance = (a, b) => {
  const matrix = [];
  for (let i = 0; i <= b.length; i++) matrix[i] = [i];
  for (let j = 0; j <= a.length; j++) matrix[0][j] = j;

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          Math.min(matrix[i][j - 1] + 1, matrix[i - 1][j] + 1),
        );
      }
    }
  }
  return matrix[b.length][a.length];
};

/**
 * Normalizes a name for fuzzy matching
 */
export const normalizeForFuzzy = (name) => {
  if (!name) return "";
  let val = name.toLowerCase();
  const suffixes = [
    "pvt",
    "ltd",
    "limited",
    "pharmaceuticals",
    "pharma",
    "labs",
    "laboratory",
    "laboratories",
    "healthcare",
    "health",
    "med",
    "medical",
    "associates",
    "associatas",
    "agency",
    "agencies",
    "enterprise",
    "enterprises",
  ];

  // Remove suffixes
  suffixes.forEach((s) => {
    const reg = new RegExp("\\b" + s + "\\b", "gi");
    val = val.replace(reg, "");
  });

  // Remove non-alphanumeric
  val = val.replace(/[^a-z0-9\s]/g, "");

  // Tokenize, Sort, and Join (handles "Sun Pharma" vs "Pharma Sun")
  let tokens = val.split(/\s+/).filter((t) => t.length > 0);
  tokens.sort();
  return tokens.join("");
};

/**
 * Checks if two strings are a close match (typo tolerant)
 */
export const isCloseMatch = (input, target) => {
  if (!input || !target) return false;

  const s1 = input.toLowerCase().trim();
  const s2 = target.toLowerCase().trim();

  // 1. Structural Match (Indifferent to spaces/order)
  const n1 = normalizeForFuzzy(s1);
  const n2 = normalizeForFuzzy(s2);

  if (n1 === n2 || n1.includes(n2) || n2.includes(n1)) return true;

  // 2. Global Typo Tolerance (for concatenated strings like "phramasun")
  if (n1.length > 3 && n2.length > 3) {
    const globalDist = getLevenshteinDistance(n1, n2);
    if (globalDist <= (n1.length > 7 ? 2 : 1)) return true;
  }

  // 3. Word-Level Typo Tolerance
  const words1 = s1.split(/\s+/);
  const words2 = s2.split(/\s+/);

  return words1.some((w1) => {
    if (w1.length < 3) return false;
    return words2.some((w2) => {
      if (w2.length < 3) return false;
      const distance = getLevenshteinDistance(w1, w2);
      const threshold = w2.length > 6 ? 2 : 1;
      return distance <= threshold;
    });
  });
};

/**
 * Strips non-alphanumerics and lowercases a string
 */
export const cleanAlphaNumeric = (str) => {
  return (str || "").toLowerCase().replace(/[^a-z0-9]/g, "");
};

/**
 * Computes a search relevance match score between query `input` and `target`.
 * Returns 0 if there is no match.
 * Returns > 0 if matched, where higher score = better match / higher rank.
 *
 * Handles:
 * - Exact full match (score 1000)
 * - Exact prefix match (score 900)
 * - Word boundary prefix (score 850)
 * - Exact phrase substring match (score 800)
 * - Concatenated match without spaces, e.g. "hydracool" matching "ISCLINICAL HYDRA COOL SERUM" (score 700-750)
 * - Multi-token / unordered match, e.g. "HYDRA ISCLINICAL" or "SERUM COOL" (score 500-550)
 * - Single-token typo tolerance via Levenshtein (score 200)
 */
export const getSearchMatchScore = (input, target) => {
  if (!input || !target) return 0;

  const rawInput = input.toLowerCase().trim();
  const rawTarget = target.toLowerCase().trim();

  if (!rawInput || !rawTarget) return 0;

  // 1. Exact match (Highest rank)
  if (rawTarget === rawInput) return 1000;

  // 2. Exact prefix match (e.g. "isclinical hydra" matches "isclinical hydra cool serum")
  if (rawTarget.startsWith(rawInput)) return 900;

  // 3. Exact word boundary prefix (e.g. target contains a word starting with query)
  const words = rawTarget.split(/\s+/);
  if (words.some((w) => w.startsWith(rawInput))) return 850;

  // 4. Exact phrase substring match anywhere in target
  if (rawTarget.includes(rawInput)) return 800;

  const cleanInput = cleanAlphaNumeric(rawInput);
  const cleanTarget = cleanAlphaNumeric(rawTarget);

  // 5. Concatenated / no-space match (e.g. "hydracool" in "isclinical hydra cool serum")
  if (cleanInput.length >= 3 && cleanTarget.includes(cleanInput)) {
    if (cleanTarget.startsWith(cleanInput)) return 750;
    return 700;
  }

  // 6. Multi-token match (all tokens in input must be matched in target)
  const tokens = rawInput.split(/\s+/).filter(Boolean);
  if (tokens.length > 0) {
    let allTokensMatched = true;
    let inOrder = true;
    let lastFoundIndex = -1;

    for (const token of tokens) {
      const cleanTok = cleanAlphaNumeric(token);
      let matched = false;

      // Check substring in rawTarget
      const idx = rawTarget.indexOf(token);
      if (idx !== -1) {
        matched = true;
        if (idx < lastFoundIndex) {
          inOrder = false;
        }
        lastFoundIndex = idx;
      } else if (cleanTok.length >= 2 && cleanTarget.includes(cleanTok)) {
        matched = true;
      } else if (isCloseMatch(token, rawTarget)) {
        matched = true;
      }

      if (!matched) {
        allTokensMatched = false;
        break;
      }
    }

    if (allTokensMatched) {
      let score = 500;
      if (inOrder && tokens.length > 1) {
        score += 50; // Bonus for matching in original token order
      }
      return score;
    }
  }

  // 7. Fallback: typo tolerance for single-word queries only (>= 3 chars)
  if (tokens.length <= 1 && rawInput.length >= 3 && isCloseMatch(rawInput, rawTarget)) {
    return 200;
  }

  return 0;
};

/**
 * Checks if input matches target across multi-token, concatenated, or typo matching
 */
export const multiTokenMatch = (input, target) => {
  return getSearchMatchScore(input, target) > 0;
};
